import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaCamera, FaTrash } from 'react-icons/fa';
import api from '../services/api';
import documentService from '../services/documentService';

const ROLE_CONFIG = {
  admin:               { label: 'Admin',        color: '#dc3545' },
  responsable:         { label: 'Responsable',  color: '#ffc107' },
  pointeur:            { label: 'Pointeur',     color: '#0d6efd' },
  responsable_budget:  { label: 'Resp. Budget', color: '#6f42c1' },
  achat:               { label: 'Achat',        color: '#0d9488' }
};

const ProfileModal = ({ show, onHide, user, photoProfilId, onPhotoChanged }) => {
  const [photoUrl, setPhotoUrl]     = useState(null);
  const [loadingImg, setLoadingImg] = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [error, setError]           = useState('');
  const fileInputRef = useRef();

  // Charger l'image quand le modal s'ouvre ou que l'ID change
  useEffect(() => {
    let revoked = false;
    let objectUrl = null;

    if (show && photoProfilId) {
      setLoadingImg(true);
      documentService.getDocumentObjectUrl(photoProfilId)
        .then(url => {
          if (revoked) { URL.revokeObjectURL(url); return; }
          objectUrl = url;
          setPhotoUrl(url);
        })
        .catch(() => setPhotoUrl(null))
        .finally(() => setLoadingImg(false));
    } else {
      setPhotoUrl(null);
    }

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [show, photoProfilId]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/users/me/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onPhotoChanged(res.data.photo_profil_id);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    setError('');
    setUploading(true);
    try {
      await api.delete('/users/me/photo');
      onPhotoChanged(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setUploading(false);
    }
  };

  const roleConfig = ROLE_CONFIG[user?.role] || { label: user?.role || '—', color: '#999' };
  const initials = (user?.name || '?')
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header
        closeButton
        style={{ backgroundColor: '#f5f5db', borderBottom: '2px solid #6d4e36' }}
      >
        <Modal.Title style={{ color: '#6d4e36', fontSize: '1rem', fontWeight: 'bold' }}>
          Mon profil
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="text-center py-4" style={{ backgroundColor: '#fafaf0' }}>
        {/* Message de bienvenue */}
        <h5 style={{ color: '#6d4e36', marginBottom: 6 }}>
          Bonjour, {user?.name}&nbsp;!
        </h5>
        <div style={{ marginBottom: 20 }}>
          <Badge
            style={{
              backgroundColor: roleConfig.color,
              fontSize: '0.8rem',
              padding: '4px 10px'
            }}
          >
            {roleConfig.label}
          </Badge>
        </div>

        {/* Avatar */}
        <div
          style={{
            width: 110,
            height: 110,
            borderRadius: '50%',
            margin: '0 auto 16px',
            border: '3px solid #6d4e36',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#e8ddd4',
            fontSize: 36,
            fontWeight: 'bold',
            color: '#6d4e36',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          {loadingImg ? (
            <Spinner animation="border" size="sm" style={{ color: '#6d4e36' }} />
          ) : photoUrl ? (
            <img
              src={photoUrl}
              alt="profil"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            initials
          )}
        </div>

        {error && (
          <Alert variant="danger" className="py-1 px-2 small mb-3">
            {error}
          </Alert>
        )}

        {/* Boutons */}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <div className="d-flex justify-content-center gap-2 flex-wrap">
          <Button
            size="sm"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            style={{ backgroundColor: '#6d4e36', borderColor: '#6d4e36' }}
          >
            {uploading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FaCamera className="me-1" />
            )}
            {photoProfilId ? 'Changer la photo' : 'Ajouter une photo'}
          </Button>

          {photoProfilId && (
            <Button
              size="sm"
              variant="outline-danger"
              disabled={uploading}
              onClick={handleDelete}
              title="Supprimer la photo"
            >
              <FaTrash />
            </Button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ProfileModal;
